import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PropertyCard from './PropertyCard.jsx';

const CARD_WIDTH = 300;
const GAP = 12;

const MapLeasePropertySlider = forwardRef(({ 
  properties, 
  selectedId, 
  onSelect, 
  onView, 
  onCardInView, 
  showAlert, 
  wishlistItems = [],
  onWishlistToggle
}, ref) => {
  const parentRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const isScrollingRef = useRef(false);
  const lastSelectedIdRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const virt = useVirtualizer({
    horizontal: true,
    count: properties.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_WIDTH + GAP,
    overscan: 5,
  });

  // Get current visible card index
  const getCurrentVisibleIndex = () => {
    if (!parentRef.current) return 0;
    const scrollLeft = parentRef.current.scrollLeft;
    const index = Math.round(scrollLeft / (CARD_WIDTH + GAP));
    return Math.min(index, properties.length - 1);
  };

  // Scroll to specific card
  const scrollToCard = (index) => {
    if (!parentRef.current || index < 0 || index >= properties.length) return;
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    
    // Handle infinite loop - wrap around
    let targetIndex = index;
    if (targetIndex >= properties.length) {
      targetIndex = 0;
    } else if (targetIndex < 0) {
      targetIndex = properties.length - 1;
    }
    
    const scrollPosition = targetIndex * (CARD_WIDTH + GAP);
    
    parentRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    
    setCurrentIndex(targetIndex);
    
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  };

  // Scroll left with infinite loop
  const scrollLeft = () => {
    if (!parentRef.current) return;
    const currentIdx = getCurrentVisibleIndex();
    let newIndex = currentIdx - 1;
    
    // Loop to last card if at first
    if (newIndex < 0) {
      newIndex = properties.length - 1;
    }
    
    scrollToCard(newIndex);
    
    // Select the card
    const property = properties[newIndex];
    if (property && onSelect) {
      onSelect(property);
    }
  };

  // Scroll right with infinite loop
  const scrollRight = () => {
    if (!parentRef.current) return;
    const currentIdx = getCurrentVisibleIndex();
    let newIndex = currentIdx + 1;
    
    // Loop to first card if at last
    if (newIndex >= properties.length) {
      newIndex = 0;
    }
    
    scrollToCard(newIndex);
    
    // Select the card
    const property = properties[newIndex];
    if (property && onSelect) {
      onSelect(property);
    }
  };

  // Scroll to specific dot index (for carousel indicators)
  const scrollToDot = (index) => {
    if (index < 0 || index >= properties.length) return;
    scrollToCard(index);
    
    // Select the card
    const property = properties[index];
    if (property && onSelect) {
      onSelect(property);
    }
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    scrollToCard: scrollToCard,
    scrollToDot: scrollToDot,
    getCardIndex: (propertyId) => {
      return properties.findIndex(p => p.id === propertyId);
    }
  }));

  // Auto-scroll when selectedId changes (from marker click)
  useEffect(() => {
    if (!selectedId || !properties.length) return;
    
    if (selectedId !== lastSelectedIdRef.current) {
      const index = properties.findIndex(p => p.id === selectedId);
      
      if (index !== -1) {
        const timer = setTimeout(() => {
          scrollToCard(index);
        }, 200);
        
        return () => clearTimeout(timer);
      }
    }
    
    lastSelectedIdRef.current = selectedId;
  }, [selectedId, properties]);

  // Update current index on scroll
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    
    let timeout;
    const onScroll = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (isScrollingRef.current) return;
        
        const newIndex = getCurrentVisibleIndex();
        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
          const property = properties[newIndex];
          if (property && property.id !== selectedId) {
            onCardInView?.(property);
          }
        }
      }, 100);
    };
    
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(timeout);
    };
  }, [properties, selectedId, onCardInView, currentIndex]);

  // Check for scroll arrows
  useEffect(() => {
    const checkArrows = () => {
      if (parentRef.current) {
        const hasScroll = parentRef.current.scrollWidth > parentRef.current.clientWidth;
        setShowArrows(hasScroll);
      }
    };
    checkArrows();
    window.addEventListener('resize', checkArrows);
    return () => window.removeEventListener('resize', checkArrows);
  }, [properties]);

  if (!properties || properties.length === 0) {
    return null;
  }

  return (
    <Box sx={{
      position: 'fixed', 
      bottom: 30,
      left: 0, 
      right: 0,
      height: 160,
      zIndex: 1100,
      px: 1,
    }}>
      <Box sx={{ position: 'relative', height: '100%' }}>
        {/* Left Arrow */}
        {showArrows && (
          <IconButton 
            onClick={scrollLeft}
            sx={{
              position: 'absolute',
              left: -8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1002,
              bgcolor: 'white',
              boxShadow: 3,
              width: 28,
              height: 28,
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Slider Container */}
        <Box
          ref={parentRef}
          sx={{
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'thin',
            px: showArrows ? 3 : 1,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: '#f1f1f1', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#888', borderRadius: 4 },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              width: virt.getTotalSize(),
            }}
          >
            {virt.getVirtualItems().map((virtualItem) => {
              const property = properties[virtualItem.index];
              if (!property) return null;
              
              const isSelected = property.id === selectedId;
              
              return (
                <Box
                  key={property.id}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateX(${virtualItem.start}px)`,
                    width: CARD_WIDTH,
                    height: '100%',
                    mr: `${GAP}px`,
                  }}
                >
                  <PropertyCard
                    property={property}
                    selected={isSelected}
                    onClick={() => onSelect(property)}
                    onView={() => onView(property)}
                    showAlert={showAlert}
                    wishlistItems={wishlistItems}
                    onWishlistToggle={onWishlistToggle}
                    isSaved={() => wishlistItems?.some(item => item.property_id === property.id)}
                    toggleSave={() => onWishlistToggle?.(property)}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Right Arrow */}
        {showArrows && (
          <IconButton 
            onClick={scrollRight}
            sx={{
              position: 'absolute',
              right: -8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1002,
              bgcolor: 'white',
              boxShadow: 3,
              width: 28,
              height: 28,
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Carousel Dots/Indicators */}
        {properties.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '8px',
              zIndex: 1002,
            }}
          >
            {properties.map((_, index) => (
              <Box
                key={index}
                onClick={() => scrollToDot(index)}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: currentIndex === index ? '#FFD600' : '#ccc',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    backgroundColor: currentIndex === index ? '#FFD600' : '#999',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default MapLeasePropertySlider;